<script>
    import dressing from "@intellectability/platform-common";

    import {create_security_error, range} from "@intellectability/platform-common/utils";
    
    // common constants
    import {ACTIONS} from "@intellectability/platform-common/constants";

    // common components
    import {
        AutoPen,
        AutoUpdateInput,
        Icon,
        toast,
        ExportButtons,
        MegaPopup
    } from "@intellectability/platform-common/sv";

    // bootstrap
    import {
        Breadcrumb,
        BreadcrumbItem,
        Button,
        Row,
        Col
    } from "@sveltestrap/sveltestrap";

    import {createForm} from "svelte-forms-lib";
    import * as yup from "yup";
    import EntityChangeHistory from "./lib/EntityChangeHistory.svelte";

    const {formID = 1} = $props(); // TODO: CHANGE THIS DEFAULT VALUE

    const {
        ICAP_MANAGE,
        ICAP_PDF,
        ICAP_GLOBAL_LIST
    } = ACTIONS;

    let loading = $state(true);

    let isExistingIcapForm = $derived(Number.isInteger(parseInt(formID)));
    
    /**
     * @type {{
     * userActions: number[]
     * businessRules: BusinessRule[]
     * applicationConfig: {[x: string]: string;}
     * userData: Object|false
     }} */
    let userSecurity = $state.raw({
        userActions: [],
        businessRules: [],
        applicationConfig: {},
        userData: false
    });

    let formDisabledBySecurity = $derived.by(function () {
        return userSecurity.userActions.includes(ICAP_MANAGE) === false;
    });

    /**
     * @typedef {object} IcapDetails
     * @property {string} id
     * @property {number} version
     * @property {string} cons_id
     * @property {string | null} person_name
     * @property {Date | null} icap_date
     * @property {number | null} icap_score
     * @property {number | null} mbi_score
     * @property {string | null} ambulatory_status
     * @property {string | null} where_residing
     * @property {string | null} who_completed
     * @property {string} created
     * @property {string} created_by
     * @property {string | null} updated
     * @property {string | null} updated_by
     * @property {string | null} signed
     * @property {string | null} signed_by
     * @property {string | null} deleted
     * @property {string | null} deleted_by
     */

    /** @type {IcapDetails} */
    let icapDetails = $state({
        id: "0",
        cons_id: "",
        person_name: "",
        icap_date: "",
        icap_score: "",
        mbi_score: "",
        ambulatory_status: "",
        where_residing: "",
        who_completed: "",
        signed: "",
        signed_by: "",
        deleted: "",
        deleted_by: ""
    });

    let icapIsSigned = $derived(icapDetails?.signed?.length > 0);
    let dateOfSignature = $derived(icapDetails.signed);
    let icapSignedBy = $derived(icapDetails.signed_by || userSecurity.userData?.id);
    let canUnsign = $derived(icapIsSigned && icapSignedBy === String(userSecurity.userData?.id || ""));

    let logDisplayed = $state(false);

    let initialValues = $derived({
        id: (icapDetails.id ?? "") + "",
        icap_date: icapDetails.icap_date ?? "",
        icap_score: (icapDetails.icap_score ?? "") + "",
        mbi_score: (icapDetails.mbi_score ?? "") + "",
        ambulatory_status: icapDetails.ambulatory_status ?? "",
        where_residing: icapDetails.where_residing ?? "",
        who_completed: icapDetails.who_completed ?? ""
    });

    let pageTitle = "ICAP Form";

    let field_margin_classes = "mb-4 mb-lg-4";

    // for dropdowns
    let ambulatoryStatuses = $state.raw([]);
    let wherePersonWasResiding = $state.raw([]);
    let whoCompletedICAP = $state.raw([]);

    const form_props = {
        validationSchema: yup.object().shape({
            icap_date: yup.date().required(),
            icap_score: yup.number().required(),
            mbi_score: yup.number().required(),
            ambulatory_status: yup.string().required(),
            where_residing: yup.string().required(),
            who_completed: yup.string().required()
        })
    };

    const {form, errors, handleSubmit, updateInitialValues, isValid} = createForm(
        {
            initialValues,
            validationSchema: form_props.validationSchema,
            onSubmit: () => {}
        });

    $effect(function () {
        // allows us to update the initial form values when changed via an
        // api call (update or load)
        updateInitialValues(initialValues);
    });

    $effect(function load() {
        dressing.api.get_user_security().then(
            (
                response
            ) => {
                if (response.success === true) {
                    const {business_rules, application_config, logged_in_user, logged_in} = response;

                    const securityData = {
                        businessRules: business_rules || [],
                        userActions: logged_in_user.actions || [],
                        applicationConfig: application_config || {},
                        userData: logged_in_user || false,
                    };

                    // @ts-ignore;
                    userSecurity = securityData;
                    
                    userSecurity.userData = securityData.userData;
                    userSecurity.applicationConfig = securityData.applicationConfig;
                    userSecurity.businessRules = securityData.businessRules;
                    userSecurity.userActions = securityData.userActions;

                    if (!securityData.userActions.includes(ICAP_MANAGE)) {
                        const security_error = create_security_error("App.svelte", "IcapForm",
                        ICAP_MANAGE);
                        dressing.api.report_security_error(security_error);
                    }

                    // We need to create the icap form for this consumer
                    if (parseInt(formID) > 0) {
                        dressing.get(`/cm/icap_forms/${formID}`)
                            .then(function ({data: res}) {
                                if (res.success === true) {
                                    icapDetails = res.data.icap;
                                } else {
                                    console.error("Error fetching ICAP form details", res);
                                    toast.error("Error loading ICAP form details");
                                }
                            }).catch(function (error) {
                                console.error("Error fetching ICAP form details", error);
                                toast.error("Error loading ICAP form details");
                            });
                    } else {
                        const security_error = create_security_error("App.svelte",
                            "IcapForm", ICAP_MANAGE);

                        dressing.api.report_security_error(security_error)
                            .then(function () {
                                // regardless of response, log out
                                dressing.api.logout();
                            });
                        return;
                    }

                    const needed_initial_data = [
                        dressing.get("/cm/icap_forms/lookups").then(({data: res}) => {
                            if (res.success === true) {
                                const {ambulatory_status, where_residing, who_completed} = res.data;
                                if (Array.isArray(ambulatory_status) && ambulatory_status.length > 0) {
                                    ambulatoryStatuses = ambulatory_status;
                                }
                                if (Array.isArray(where_residing) && where_residing.length > 0) {
                                    wherePersonWasResiding = where_residing;
                                }
                                if (Array.isArray(who_completed) && who_completed.length > 0) {
                                    whoCompletedICAP = who_completed;
                                }
                                return true;
                            }
                            return false;
                        })
                    ];

                    Promise.allSettled(needed_initial_data).then(function (resolved) {
                        const result = resolved.every(r => r.value === true);
                        if (result) {
                            return loading = false;
                        }
                        else {
                            toast.error("Error loading initial data");
                        }
                    });
                } else {
                    console.error("Error fetching user security data", response);
                }
            }
        );
    });

    function on_update({field, value}) {
        icapDetails[field] = value;
    }

    /**
     * Handles the signature update for the ICAP form.
     * It sends a request to update the signature status of the ICAP form, after verifying the form is validly filled.
     * @param {boolean} is_signed
     */
    function handle_signature(is_signed) {
        if (typeof is_signed === "boolean") {
            const event = new Event("submit", {bubbles: false, cancelable: true});
            handleSubmit(event).then(() => {
                // If the form is valid, we can proceed with the signature update
                if ($isValid || !is_signed) {
                    dressing.post("cm/icap_forms/update", {
                        id: formID,
                        secondary_id: "",
                        field: "signed",
                        value: is_signed
                    }).then(function ({data: res}) {
                        if (res.success === true) {
                            icapDetails.signed = res.data.icap.signed;
                            icapDetails.signed_by = res.data.icap.signed_by;
                        } else {
                            toast.error("Failed to update signature.");
                        }
                    });
                }
            }).catch((error) => {
                console.error("Error during form submission:", error);
            });
        }
    }

    function displayLogs() {
        logDisplayed = !logDisplayed;
    }
</script>

<Breadcrumb divider={" "} class="breadcrumbs mb-3">
    <BreadcrumbItem>
        Case Management
    </BreadcrumbItem>
    <BreadcrumbItem class="mx-3">
        <Icon icon="chevron-right"/>
    </BreadcrumbItem>
    <BreadcrumbItem>
        <a href="/index.php?action={ICAP_GLOBAL_LIST}">
            <Icon icon="warehouse" class="mr-1"/>
            Icaps
        </a>
    </BreadcrumbItem>
    <BreadcrumbItem class="mx-3">
        <Icon icon="chevron-right"/>
    </BreadcrumbItem>
    <BreadcrumbItem>
        View / Edit
    </BreadcrumbItem>
</Breadcrumb>
<!-- Page Title -->
<h1>
    {#if isExistingIcapForm}
        <Icon icon="warehouse" class="me-3"/>{pageTitle} for {icapDetails.person_name}
    {/if}
</h1>
<!-- Export Buttons -->
<div class="d-flex w-100 justify-content-end mb-3">
    <ExportButtons
        show={["pdf"]}
        href="/index.php?action={ICAP_PDF}&id={formID}"
    />
</div>
<!-- Form -->
<wa-callout class="mb-5 bg-primary-50 text-secondary-900 border-primary" appearance="outlined filled">
    <wa-icon slot="icon" name="circle-info" variant="regular"></wa-icon>
    All fields are required.
</wa-callout>
<form onsubmit={handleSubmit}>
    {#key icapDetails}
        <fieldset disabled={loading} id="loading-fieldset">
            <Row class="">
                <Col xs="12" lg="4" class={field_margin_classes}>
                    <AutoUpdateInput
                            fieldName="icap_date"
                            loading={loading}
                            autoupdate={isExistingIcapForm}
                            disabled={formDisabledBySecurity || icapIsSigned}
                            route={"cm/icap_forms/update"}
                            bind:defaultValue={$form.icap_date}
                            invalid={$errors.icap_date.length > 0}
                            invalidMessage="Please provide a valid date"
                            entityID={formID}
                            type="date"
                            onUpdate={on_update}
                            label="Date of ICAP"/>
                </Col>
                <Col xs="12" lg="4" class={field_margin_classes}>
                    <AutoUpdateInput
                            fieldName="icap_score"
                            loading={loading}
                            autoupdate={isExistingIcapForm}
                            disabled={formDisabledBySecurity || !isExistingIcapForm || icapIsSigned}
                            route={"cm/icap_forms/update"}
                            bind:defaultValue={$form.icap_score}
                            entityID={formID}
                            type="select"
                            invalid={$errors.icap_score.length > 0}
                            invalidMessage="Please select an ICAP Score"
                            optionsFrom={range(0, 100, 1, "string")}
                            onUpdate={on_update}
                            label="ICAP Score"/>
                </Col>
                <Col xs="12" lg="4" class={field_margin_classes}>
                    <AutoUpdateInput
                            fieldName="mbi_score"
                            loading={loading}
                            autoupdate={isExistingIcapForm}
                            disabled={formDisabledBySecurity || !isExistingIcapForm || icapIsSigned}
                            route={"cm/icap_forms/update"}
                            bind:defaultValue={$form.mbi_score}
                            entityID={formID}
                            type="select"
                            invalid={$errors.mbi_score.length > 0}
                            invalidMessage="Please select a MBI Score"
                            optionsFrom={range(-70, 0, 1, "string")}
                            onUpdate={on_update}
                            label="MBI Score"/>
                </Col>
            </Row>
            <Row class="">
                <Col xs="12" lg="12" class={field_margin_classes}>
                    <AutoUpdateInput
                            fieldName="ambulatory_status"
                            loading={loading}
                            autoupdate={isExistingIcapForm}
                            disabled={formDisabledBySecurity || !isExistingIcapForm || icapIsSigned}
                            route={"cm/icap_forms/update"}
                            bind:defaultValue={$form.ambulatory_status}
                            entityID={formID}
                            type="select"
                            invalid={$errors.ambulatory_status.length > 0}
                            invalidMessage="Please select the ambulatory status"
                            optionsFrom={ambulatoryStatuses}
                            onUpdate={on_update}
                            label={"Ambulatory Status"}/>
                </Col>
                <Col xs="12" lg="12" class={field_margin_classes}>
                    <AutoUpdateInput
                            fieldName="where_residing"
                            loading={loading}
                            autoupdate={isExistingIcapForm}
                            disabled={formDisabledBySecurity || !isExistingIcapForm || icapIsSigned}
                            route={"cm/icap_forms/update"}
                            bind:defaultValue={$form.where_residing}
                            entityID={formID}
                            type="select"
                            invalid={$errors.where_residing.length > 0}
                            invalidMessage="Please select where the person was residing"
                            optionsFrom={wherePersonWasResiding}
                            onUpdate={on_update}
                            label={"Where Was The Person Residing When this ICAP Was Completed?"}/>
                </Col>
                <Col xs="12" lg="12" class={field_margin_classes}>
                    <AutoUpdateInput
                            fieldName="who_completed"
                            loading={loading}
                            autoupdate={isExistingIcapForm}
                            disabled={formDisabledBySecurity || !isExistingIcapForm || icapIsSigned}
                            route={"cm/icap_forms/update"}
                            bind:defaultValue={$form.who_completed}
                            entityID={formID}
                            type="select"
                            invalid={$errors.who_completed.length > 0}
                            invalidMessage="Please select who completed the ICAP"
                            optionsFrom={whoCompletedICAP}
                            onUpdate={on_update}
                            label={"Who completed the ICAP for this person?"}/>
                </Col>
            </Row>
            <Row>
                <Col xs="12" lg="6">
                    <wa-callout variant="neutral" class="rounded-2">
                        {#if !icapIsSigned}
                            <strong>I Confirm this is the most recent ICAP information on this
                                individual</strong>
                        {/if}

                        {#if icapIsSigned}
                            <h4>Submitted by</h4>
                        {/if}

                        <AutoPen
                                userID={icapSignedBy}
                                allowUnsign={canUnsign}
                                isSigned={icapIsSigned}
                                confirmButton="Submit ICAP"
                                showOptions={{
                                    name: true,
                                    credentials: true,
                                    employer: true,
                                    date: dateOfSignature,
                                    signature: true,
                                    title: true
                                }}
                                onupdate={function(result) {
                                    handle_signature(result.isSigned);
                                }}
                        />

                    </wa-callout>
                </Col>
            </Row>
            {#if !isExistingIcapForm}
                <Button color="primary" class="rounded-pill">submit</Button>
            {/if}
        </fieldset>
    {/key}
</form>
<button id="change_log_button" class="btn btn-primary btn-sm rounded-pill mt-4" onclick={displayLogs}>
    {"Change Log"}
</button>
<MegaPopup
    bind:active={logDisplayed}
    for="change_log_button"
    placement="top-start">
    <div class="pt-2 px-3 rounded-2 bg-white">
        <EntityChangeHistory entity={"icap_form"} id={$form.id}></EntityChangeHistory>
    </div>
</MegaPopup>